/**
 * section-list-interaction.js — the section list's multi-select interaction
 * layer, extracted per Phase 16's constraint C5: section-list.js imports
 * shell.js and therefore cannot be imported by a node test, so every rule that
 * decides WHAT a gesture means lives here, shell-free and jsdom-importable.
 * section-list.js keeps only the DOM glue (find the row, read the event, emit
 * the request).
 *
 * Nothing in this module writes the selection store. Gestures resolve to
 * `selection:request` payloads consumed by ui/selection-controller.js — the
 * store's single writer — which applies domain policy (shared-row collapse,
 * pick mode) that deliberately does NOT live here.
 */

/**
 * Map a pointer event's modifiers to a selection intent.
 *
 *   shift+mod → 'range-add'   (union the anchor→id range into the selection)
 *   shift     → 'range'       (REPLACE with the anchor→id range)
 *   mod       → 'toggle'      (add/remove one id)
 *   plain     → 'replace'     (today's click, byte-identical)
 *
 * @param {{metaKey?: boolean, ctrlKey?: boolean, shiftKey?: boolean}} event
 * @returns {'range-add'|'range'|'toggle'|'replace'}
 */
export function resolveClickIntent(event) {
  const mod = !!(event && (event.metaKey || event.ctrlKey));
  const shift = !!(event && event.shiftKey);
  if (shift && mod) return 'range-add';
  if (shift) return 'range';
  if (mod) return 'toggle';
  return 'replace';
}

/**
 * Map a row-level keydown to a selection action, or null when the key belongs
 * to an existing single-select binding (Enter/Space/F2/Alt+Arrow/…), which the
 * caller handles unchanged. Placed BEFORE those branches so their `!shift`
 * guards keep working.
 *
 * @param {{key: string, metaKey?: boolean, ctrlKey?: boolean, shiftKey?: boolean, altKey?: boolean}} event
 * @returns {{type: 'focus-move'|'extend', dir: -1|1} | {type: 'toggle-focused'} | null}
 */
export function resolveRowKeyIntent(event) {
  if (!event) return null;
  const mod = !!(event.metaKey || event.ctrlKey);
  const shift = !!event.shiftKey;
  const alt = !!event.altKey;
  const key = event.key;

  if ((key === 'ArrowUp' || key === 'ArrowDown') && !mod && !alt) {
    const dir = key === 'ArrowUp' ? -1 : 1;
    return shift ? { type: 'extend', dir } : { type: 'focus-move', dir };
  }
  if ((key === ' ' || key === 'Spacebar') && mod && !shift && !alt) {
    return { type: 'toggle-focused' };
  }
  return null;
}

/**
 * The id Shift+Arrow extends to: one step from the CURRENT LEAD along the
 * visible candidate order (never from the focused row — the lead owns keyboard
 * range extension, and focus follows it). Clamps at the ends. With no lead the
 * extension starts from `fallbackId` (the focused row), so Shift+↓ on a fresh
 * list selects that row rather than nothing.
 *
 * @param {string|null} leadId
 * @param {string[]} order — visible candidate ids, document order
 * @param {-1|1} dir
 * @param {string|null} [fallbackId]
 * @returns {string|null}
 */
export function extendTargetFromLead(leadId, order, dir, fallbackId = null) {
  if (!Array.isArray(order) || order.length === 0) return null;
  if (!leadId) return fallbackId && order.includes(fallbackId) ? fallbackId : null;
  const i = order.indexOf(leadId);
  if (i === -1) return null;
  const next = Math.min(order.length - 1, Math.max(0, i + dir));
  return order[next];
}

/**
 * Roving row focus: given the currently focused id, the id that ↑/↓ should
 * move focus to. Clamps at the ends (no wrap — matches the reorder keys).
 *
 * @param {string|null} focusedId
 * @param {string[]} order
 * @param {-1|1} dir
 * @returns {string|null}
 */
export function nextFocusTarget(focusedId, order, dir) {
  if (!Array.isArray(order) || order.length === 0) return null;
  const i = focusedId ? order.indexOf(focusedId) : -1;
  if (i === -1) return dir === 1 ? order[0] : order[order.length - 1];
  return order[Math.min(order.length - 1, Math.max(0, i + dir))];
}

/**
 * Bulk-bar copy. Selection can outlive a filter change, so members hidden by
 * the current filter are disclosed rather than silently counted — deleting an
 * invisible section is a data-loss surprise.
 *
 * @param {string[]} selectedIds
 * @param {Iterable<string>} visibleIds — ids currently visible in the list
 * @returns {{count: number, hiddenCount: number, text: string}}
 */
export function summarizeSelection(selectedIds, visibleIds) {
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  const visible = visibleIds instanceof Set ? visibleIds : new Set(visibleIds || []);
  const hiddenCount = ids.filter((id) => !visible.has(id)).length;
  const text =
    `${ids.length} selected` + (hiddenCount > 0 ? ` · ${hiddenCount} hidden by filter` : '');
  return { count: ids.length, hiddenCount, text };
}

/**
 * The live-region utterance for a selection change. One sentence, no chrome —
 * the bulk bar's visible count is deliberately not a live region (two regions
 * describing one state double-announce), so this is the only thing a screen
 * reader hears.
 *
 * @param {{size: number, hiddenCount?: number}} summary
 * @returns {string}
 */
export function selectionAnnouncement({ size, hiddenCount = 0, noun = 'section', nounPlural }) {
  if (!size) return 'Selection cleared';
  // `noun` defaults to 'section' so every existing call site keeps its exact
  // wording. It is parameterised because the collection browser announces
  // projects, previews and snapshots through this same helper, and a bulk bar
  // that tells a screen-reader user "3 sections selected" over a list of
  // projects is worse than saying nothing.
  const plural = nounPlural || `${noun}s`;
  const base = size === 1 ? `1 ${noun} selected` : `${size} ${plural} selected`;
  return hiddenCount > 0 ? `${base}, ${hiddenCount} hidden by filter` : base;
}
